<template>
  <div class="property-row">
    <!-- A ficha do campo (escolha do usuário): cada conteúdo na forma dele — o que o campo é numa
         frase, os nomes técnicos em código, as marcas em selos, a fórmula e as regras como código.

         Este comentário mora DENTRO da raiz, e não antes dela: antes, ele vira um segundo nó na raiz do
         componente, e o vuedraggable, que pede um elemento só por item, quebrava ao começar o arrasto
         ("Cannot read properties of null (reading 'element')" — relato do usuário). -->
    <v-avatar size="32" color="primary" variant="tonal" class="property-row-icon">
      <v-icon size="small">{{ face.icon }}</v-icon>
    </v-avatar>
    <div class="property-row-text">
      <div class="property-row-head">
        <span class="property-row-name text-subtitle-1">{{ face.name }}</span>
        <span class="ontos-code text-caption text-medium-emphasis" :title="$tt('alias-url')">{{ face.alias }}</span>
        <span v-if="face.formulaName" class="ontos-code text-caption text-medium-emphasis"
              :title="$tt('formula-name')">{{ face.formulaName }}</span>
      </div>
      <div class="property-row-description text-body-2 text-medium-emphasis">{{ face.description }}</div>
      <!-- As opções da seleção como o valor aparece no item e no quadro: o nome no fundo da cor dela
           (pedido do usuário) -->
      <div v-if="face.options.length" class="property-row-options">
        <ontos-colored-value v-for="option of face.options" :key="option.id" :option="option" form="chip"/>
      </div>
      <!-- No telefone, as marcas embaixo da frase, junto do texto: ao lado dos botões, elas desciam com
           eles para uma linha solta (relato do usuário). Nas outras larguras, ao lado dos botões -->
      <ontos-property-marks v-if="!marksBeside && face.marks.length" :marks="face.marks" class="property-row-marks"/>
      <!-- A fórmula e as regras como código, com o símbolo de cada; a mensagem da regra ao passar o mouse -->
      <div v-if="face.formula" class="property-row-code">
        <v-icon size="x-small" class="text-medium-emphasis">mdi-function-variant</v-icon>
        <code class="ontos-code text-truncate" :title="face.formula">{{ face.formula }}</code>
      </div>
      <div v-for="(rule, index) of face.rules" :key="index" class="property-row-code" :title="rule.message">
        <v-icon size="x-small" class="text-medium-emphasis">mdi-check-decagram</v-icon>
        <code class="ontos-code text-truncate">{{ rule.rule }}</code>
      </div>
      <!-- Visível quando e editável quando (pedido do usuário), com o ícone da pergunta — o do editor de
           acesso — e a pergunta escrita: só o ícone não dizia que a fórmula é uma condição -->
      <div v-for="access of face.access" :key="access.kind" class="property-row-code">
        <v-icon size="x-small" class="text-medium-emphasis">{{ accessIcons[access.kind] }}</v-icon>
        <span class="text-medium-emphasis text-no-wrap">{{ access.label }}</span>
        <code class="ontos-code text-truncate">{{ access.formula }}</code>
      </div>
    </div>
    <!-- As ações sempre no alto à direita, na linha do nome, em qualquer largura — como na lista de
         itens; e, fora do telefone, as marcas antes delas -->
    <div v-if="canWrite || (marksBeside && face.marks.length)" class="property-row-actions">
      <ontos-property-marks v-if="marksBeside && face.marks.length" :marks="face.marks"/>
      <ontos-action-group v-if="canWrite">
        <v-btn variant="text" icon :title="$tt('edit')" @click="$emit('edit', property)">
          <v-icon size="small">mdi-pencil</v-icon>
        </v-btn>
        <v-btn variant="text" icon color="error" :title="$tt('delete')" @click="$emit('delete', property)">
          <v-icon size="small">mdi-delete</v-icon>
        </v-btn>
      </ontos-action-group>
      <!-- A alça de arrastar, à direita, como nas listas de opções e situações (pedido do usuário). Só
           pela alça: no telefone, rolar a aba não vira arrasto -->
      <v-icon v-if="canWrite" class="ontos-grip text-medium-emphasis" :title="$tt('property-reorder')">mdi-drag</v-icon>
    </div>
  </div>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import IdeaProperty from '@/types/IdeaProperty'
import {ACCESS_ICONS} from '@/types/Idea'
import ActionGroup from '@/components/shared/ActionGroup.vue'
import ColoredValue from '@/components/shared/ColoredValue.vue'
import PropertyMarks from '@/components/design/class/PropertyMarks.vue'
import {NameResolver, PropertyFace, propertyFace} from '@/utils/propertyDefinition'

/**
 * Uma propriedade nas caixas do design da coleção — a do identificador e a das demais (pedido do
 * usuário). Uma linha de lado a lado, e não um card numa grade: a lista se ordena de cima para baixo,
 * como os campos no item; na grade, ao quebrar linha, a ordem se lia em zigue-zague.
 *
 * O que dizer sai do `propertyFace`: a mesma definição que o card dizia, nada some ao trocar a forma.
 */
@Component({emits: ['edit', 'delete'], name: 'ontos-property-row',
  components: {'ontos-action-group': ActionGroup, 'ontos-colored-value': ColoredValue,
    'ontos-property-marks': PropertyMarks}})
class PropertyRow extends Vue {
  @Prop({type: Object, required: true}) readonly property!: IdeaProperty
  /** Resolve alias de coleção/propriedade para nome legível; vem do host. */
  @Prop({type: Object, required: true}) readonly names!: NameResolver
  /** Desenho o lápis, a lixeira e a alça? Para quem desenha, num ambiente aberto. */
  @Prop({type: Boolean, default: true}) readonly canWrite!: boolean

  //Exposto ao template: o ícone de cada pergunta de acesso
  accessIcons = ACCESS_ICONS

  get face(): PropertyFace {
    return propertyFace(this.property, this.names)
  }

  /**
   * As marcas ao lado dos botões, menos no telefone (pedido do usuário): ali, ao lado dos botões, elas
   * desciam juntas para uma linha solta, e por isso vão para baixo da frase.
   */
  get marksBeside(): boolean {
    return !this.$vuetify.display.xs
  }
}

export default toNative(PropertyRow)
</script>

<style scoped>
/* Uma plaquinha (escolha do usuário): a superfície do app, de cantos arredondados, sobre o fundo da
   caixa — uma peça que se arrasta. Dentro, o ícone, o texto na largura que sobra e, no alto à direita,
   as ações, em qualquer largura: o que cresce, no telefone, é o texto, que quebra linha */
.property-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 8px 10px 12px;
  background-color: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
}

.property-row-icon {
  margin-top: 2px;
}

.property-row-text {
  flex: 1 1 auto;
  min-width: 0;
}

.property-row-options {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

/* As opções e, no telefone, as marcas: uma linha própria embaixo da frase */
.property-row-options,
.property-row-marks {
  margin-top: 4px;
}

.property-row-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: 8px;
}

.property-row-name {
  font-weight: 500;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.property-row-code {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  margin-top: 2px;
  font-size: 0.8125rem;
}

.property-row-actions {
  flex: none;
  display: flex;
  align-items: center;
  gap: 4px;
}
</style>
